import React, { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import { format, addDays } from "date-fns";

const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function RotaCalendar() {
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [shiftTypes, setShiftTypes] = useState([]);

  function getMonday(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }

  useEffect(() => {
    supabase.from("employees").select("*").then(({ data }) => setEmployees(data || []));
    supabase.from("shift_types").select("*").order("order_num", { ascending: true }).then(({ data }) => setShiftTypes(data || []));
  }, []);

  useEffect(() => {
    fetchAssignments();
    // eslint-disable-next-line
  }, [weekStart]);

  const fetchAssignments = async () => {
    const week_start_str = weekStart.toISOString().slice(0, 10);
    const { data } = await supabase
      .from("rota_assignment")
      .select("*")
      .eq("week_start", week_start_str);
    setAssignments(data || []);
  };

  // Helper to get employee object
  const getEmployee = (id) => employees.find(e => e.id === id) || {};

  // Helper to get shift type names
  const shiftTypeNames = shiftTypes.map(s => s.name);

  // Build table: rows = days, columns = shift types
  const tableRows = days.map((day, dayIdx) => {
    // For each shift type, get all assignments for this day/shift
    const cells = shiftTypeNames.map(shiftType => {
      const cellAssignments = assignments.filter(
        a => a.day === dayIdx && a.shift_name === shiftType
      );
      if (cellAssignments.length === 0) return null;
      return cellAssignments.map(a => {
        const emp = getEmployee(a.employee_id);
        const rate = emp.hourly_rate || 9.5;
        const hours = a.hours || 0;
        const isDelivery = a.shift_name === "Delivery";
        return (
          <div
            key={a.id}
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: 6,
              padding: 6,
              marginBottom: 4,
              background: "#fafbfc",
              fontSize: 14
            }}
          >
            <b>{emp.name}</b><br />
            {a.shift_start && a.shift_end ? (
              <>
                {a.shift_start}-{a.shift_end}
                <br />
              </>
            ) : null}
            {hours ? `${hours}h` : ""}
            {isDelivery
              ? <><br /><span style={{ color: "#888" }}>£0.00</span></>
              : rate && hours
                ? <><br />£{(hours * rate).toFixed(2)}</>
                : null}
          </div>
        );
      });
    });
    // Calculate total pay for the day (Delivery is £0.00)
    const totalPay = assignments
      .filter(a => a.day === dayIdx)
      .reduce((sum, a) => {
        const emp = getEmployee(a.employee_id);
        const rate = emp.hourly_rate || 9.5;
        const hours = a.hours || 0;
        if (a.shift_name === "Delivery") return sum;
        return sum + (hours * rate);
      }, 0);
    return {
      day,
      cells,
      totalPay: `£${totalPay.toFixed(2)}`
    };
  });

  // Calculate totals for the week
  const totalShopHours = assignments.filter(a => a.shift_name !== "Delivery").reduce((sum, a) => sum + (a.hours || 0), 0);
  const totalDeliveryHours = assignments.filter(a => a.shift_name === "Delivery").reduce((sum, a) => sum + (a.hours || 0), 0);
  const totalAmount = assignments.filter(a => a.shift_name !== "Delivery").reduce((sum, a) => {
    const emp = employees.find(e => e.id === a.employee_id) || {};
    const rate = emp.hourly_rate || 9.5;
    return sum + ((a.hours || 0) * rate);
  }, 0);

  // Employee summary (with shop, delivery, total hours, and total pay)
  const summary = employees.map(emp => {
    const empAssignments = assignments.filter(a => a.employee_id === emp.id);
    const deliveryHours = empAssignments
      .filter(a => a.shift_name === "Delivery")
      .reduce((sum, a) => sum + (a.hours || 0), 0);
    const nonDeliveryAssignments = empAssignments.filter(a => a.shift_name !== "Delivery");
    const shopHours = nonDeliveryAssignments.reduce((sum, a) => sum + (a.hours || 0), 0);
    const totalHours = shopHours + deliveryHours;
    const rate = emp.hourly_rate || 9.5;
    const totalPay =
      nonDeliveryAssignments.length === 0
        ? 0
        : shopHours * rate;
    return {
      name: emp.name,
      shopHours,
      deliveryHours,
      totalHours,
      rate: `£${rate}`,
      totalPay: `£${totalPay.toFixed(2)}`
    };
  });

  // Totals for employee summary
  const totalSummary = summary.reduce(
    (acc, s) => {
      acc.shopHours += s.shopHours;
      acc.deliveryHours += s.deliveryHours;
      acc.totalHours += s.totalHours;
      acc.totalPay += parseFloat(s.totalPay.replace("£", ""));
      return acc;
    },
    { shopHours: 0, deliveryHours: 0, totalHours: 0, totalPay: 0 }
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ fontWeight: 700, fontSize: 26, marginBottom: 16 }}>
        Rota Calendar for week starting {format(weekStart, "dd/MM/yyyy")}
        <button
          style={{ marginLeft: 16, background: "#2979ff", color: "#fff", border: "none", borderRadius: 8, padding: "6px 16px", fontWeight: 600, cursor: "pointer" }}
          onClick={() => setWeekStart(addDays(weekStart, -7))}
        >Prev</button>
        <button
          style={{ marginLeft: 8, background: "#2979ff", color: "#fff", border: "none", borderRadius: 8, padding: "6px 16px", fontWeight: 600, cursor: "pointer" }}
          onClick={() => setWeekStart(addDays(weekStart, 7))}
        >Next</button>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 32, background: "#fff", borderRadius: 8, boxShadow: "0 2px 16px #0001" }}>
        <thead>
          <tr style={{ background: "#f5f7fa" }}>
            <th style={thStyle}>Day</th>
            {shiftTypeNames.map(shift => (
              <th key={shift} style={thStyle}>{shift}</th>
            ))}
            <th style={thStyle}>Total</th>
          </tr>
        </thead>
        <tbody>
          {tableRows.map((row, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
              <td style={tdStyle}><b>{row.day}</b></td>
              {shiftTypeNames.map((shift, j) => (
                <td key={shift} style={tdStyle}>
                  {row.cells[j] && row.cells[j].length > 0
                    ? row.cells[j]
                    : <span style={{ color: "#bbb" }}>-</span>}
                </td>
              ))}
              <td style={tdStyle}><b>{row.totalPay}</b></td>
            </tr>
          ))}
          {/* Totals row */}
          <tr style={{ background: "#f5f7fa", fontWeight: 700 }}>
            <td style={tdStyle}>Totals</td>
            {shiftTypeNames.map((shift, j) => {
              if (shift === "Delivery") {
                return (
                  <td key={shift} style={tdStyle}>
                    {totalDeliveryHours}h
                  </td>
                );
              }
              if (j === 0) {
                // Only show shop hours in the first shift column for clarity
                return (
                  <td key={shift} style={tdStyle}>
                    {totalShopHours}h
                  </td>
                );
              }
              return <td key={shift} style={tdStyle}></td>;
            })}
            <td style={tdStyle}>£{totalAmount.toFixed(2)}</td>
          </tr>
        </tbody>
      </table>

      <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 12 }}>Employee Summary</div>
      <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: 8, boxShadow: "0 2px 16px #0001", marginBottom: 32 }}>
        <thead>
          <tr style={{ background: "#f5f7fa" }}>
            <th style={thStyle}>Employee</th>
            <th style={thStyle}>Shop Hours</th>
            <th style={thStyle}>Delivery Hours</th>
            <th style={thStyle}>Total Hours</th>
            <th style={thStyle}>Hourly Rate</th>
            <th style={thStyle}>Total Wages</th>
          </tr>
        </thead>
        <tbody>
          {summary.map((s, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
              <td style={tdStyle}>{s.name}</td>
              <td style={tdStyle}>{s.shopHours}</td>
              <td style={tdStyle}>{s.deliveryHours}</td>
              <td style={tdStyle}>{s.totalHours}</td>
              <td style={tdStyle}>{s.rate}</td>
              <td style={tdStyle}>{s.totalPay}</td>
            </tr>
          ))}
          {/* Totals row */}
          <tr style={{ background: "#f5f7fa", fontWeight: 700 }}>
            <td style={tdStyle}>Totals</td>
            <td style={tdStyle}>{totalSummary.shopHours}</td>
            <td style={tdStyle}>{totalSummary.deliveryHours}</td>
            <td style={tdStyle}>{totalSummary.totalHours}</td>
            <td style={tdStyle}></td>
            <td style={tdStyle}>£{totalSummary.totalPay.toFixed(2)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const thStyle = {
  padding: "10px 8px",
  fontWeight: 700,
  textAlign: "left",
  borderBottom: "2px solid #e0e0e0"
};
const tdStyle = {
  padding: "8px 8px",
  fontWeight: 400,
  textAlign: "left",
  verticalAlign: "top"
};